/**
 * Your world, in groups: its size; the invitation (a small QR code, its code, Share and Copy link);
 * who's on the planet and who else is in it; the testing tools (development builds); leaving and
 * signing out.
 */
import type { JSX } from 'react';

import { requestLobby } from '@game/session/lobby';
import { closePanel, openPanel } from '@game/view/panels';
import { t } from '@i18n/index';
import { Sheet } from '@ui/components/Sheet';
import { useGame, useGameState, useLocale } from '@ui/hooks';

import { Invite } from './Invite';
import { People } from './People';
import { Testing } from './Testing';

import './world.css';

const DEV_BUILD = import.meta.env.DEV || import.meta.env.VITE_DEV_TOOLS === 'true';

export function WorldPanel({ confirmLeave }: { confirmLeave: boolean }): JSX.Element | null {
  useLocale();
  const game = useGame();
  const world = useGameState((s) => s.world);
  const view = useGameState((s) => s.view);
  const devTools = useGameState((s) => s.devTools);
  if (!world) return null;
  const tiles = view?.kind.length ?? 0;
  const close = (): void => closePanel(game);

  return (
    <Sheet id="world" title={world.name} onClose={close} className="world">
      {view && <p className="note size">{t('world.info', { tiles })}</p>}
      <Invite world={world} />
      <People />
      {devTools && DEV_BUILD && <Testing game={game} close={close} />}
      {confirmLeave ? (
        <div className="confirm">
          <p>{t('world.leave-confirm', { world: world.name })}</p>
          <div className="row-btns">
            <button
              type="button"
              className="btn warn"
              onClick={() => {
                close();
                requestLobby(game, { kind: 'leave' });
              }}
            >
              {t('world.leave')}
            </button>
            <button type="button" className="btn quiet" onClick={() => openPanel(game, 'world')}>
              {t('world.stay')}
            </button>
          </div>
        </div>
      ) : (
        <div className="row-btns">
          <button type="button" className="btn quiet warn" onClick={() => openPanel(game, 'leave')}>
            {t('world.leave')}
          </button>
          <button
            type="button"
            className="btn quiet"
            onClick={() => {
              close();
              requestLobby(game, { kind: 'logout' });
            }}
          >
            {t('welcome.logout')}
          </button>
        </div>
      )}
    </Sheet>
  );
}
